import { getCurrentOrgId, getCurrentRoleCode } from './client';

const DEFAULT_TTL_MS = 15_000;

interface PendingEntry<T> {
  promise: Promise<T>;
}

interface ResolvedEntry<T> {
  value: T;
  expiresAt: number;
}

type CacheEntry<T> = PendingEntry<T> | ResolvedEntry<T>;

const entries = new Map<string, CacheEntry<unknown>>();

function scopedKey(key: string): string {
  const orgId = getCurrentOrgId() ?? 'no-org';
  const roleCode = getCurrentRoleCode() ?? 'no-role';
  return `${orgId}:${roleCode}:${key}`;
}

function isResolved<T>(entry: CacheEntry<T>): entry is ResolvedEntry<T> {
  return 'value' in entry;
}

/**
 * Shares read-only dashboard requests within an organization/role scope.
 *
 * Concurrent callers reuse one in-flight promise. A successful response is
 * retained briefly so returning to the dashboard does not immediately refetch
 * every card. Rejections are never cached, and the scope prevents data from
 * crossing organization switches.
 */
export function queryDashboard<T>(
  key: string,
  loader: () => Promise<T>,
  ttlMs = DEFAULT_TTL_MS,
): Promise<T> {
  const cacheKey = scopedKey(key);
  const existing = entries.get(cacheKey) as CacheEntry<T> | undefined;

  if (existing) {
    if (!isResolved(existing)) return existing.promise;
    if (existing.expiresAt > Date.now()) return Promise.resolve(existing.value);
    entries.delete(cacheKey);
  }

  const pending: PendingEntry<T> = {
    promise: Promise.resolve().then(loader),
  };
  entries.set(cacheKey, pending as CacheEntry<unknown>);

  pending.promise.then(
    (value) => {
      // A clear or newer request must not be overwritten by this older result.
      if (entries.get(cacheKey) === pending) {
        entries.set(cacheKey, {
          value,
          expiresAt: Date.now() + Math.max(0, ttlMs),
        });
      }
    },
    () => {
      if (entries.get(cacheKey) === pending) entries.delete(cacheKey);
    },
  );

  return pending.promise;
}

/** Test/logout escape hatch. Normal expiry is automatic. */
export function clearDashboardQueryCache(): void {
  entries.clear();
}
